import { Directive, EventEmitter, Output } from '@angular/core';
import {
  AsyncValidatorFn,
  ControlValueAccessor,
  FormControlName,
  UntypedFormGroup,
  FormGroupDirective,
  NgControl,
  ValidatorFn,
} from '@angular/forms';
import { isObject, touchAll } from '@libraries/helpers';
import { sectionValidator } from './section-validator';

@Directive()
export abstract class SectionValueAccessor<T extends Record<string, any>>
  implements ControlValueAccessor
{
  @Output() sectionChange = new EventEmitter<T>();
  disabled = false;
  sectionForm: UntypedFormGroup = new UntypedFormGroup({});
  validators: ValidatorFn[] = [];
  asyncValidators: AsyncValidatorFn[] = [];
  private _value = {} as T;
  private _onChange: (value: T) => void = () => {};
  private _onTouched = () => {};

  protected constructor(public ngControl: NgControl) {
    this.ngControl.valueAccessor = this;
    setTimeout(() => {
      this._setValidators();
    });
  }

  get value(): T {
    return this._value;
  }

  set value(value: T) {
    this._value = value;
    this._onChange(this._value);
  }

  writeValue(value: T): void {
    this._value = value;

    if (isObject(this._value)) {
      this.sectionForm.patchValue(this._value);
      if (this.ngControl.touched) {
        touchAll(this.sectionForm);
      }
    }
  }

  registerOnChange(fn: (value: T) => void): void {
    // hack to avoid ExpressionChangedAfterItHasBeenCheckedError
    // see: https://github.com/angular/angular/issues/15217#issuecomment-287228219
    this._onChange = (value) => {
      Promise.resolve(null).then(() => {
        fn(value);
        this.sectionChange.emit(this._value);
      });
    };

    // we merge our CVA `value` and `sectionForm.value` incase there are keys such as `id` or `_destroy` that aren't included in `sectionForm`
    if (Object.keys(this.sectionForm.controls).length) {
      this.value = Object.assign({}, this.value, this.sectionForm.value);
    }

    // handle changes from our `sectionForm` so we can update our CVA `value`
    this.sectionForm.valueChanges.subscribe((value) => {
      // remove any disabled controls from our CVA `value`
      // this is needed because we merge our CVA `value` and `sectionForm.value` incase there are keys such as `id` or `_destroy` that aren't included in `sectionForm`
      Object.keys(this.value).forEach((key) => {
        if (this.sectionForm.get(key)?.disabled) {
          const { [key]: disabled, ...enabled } = this.value as any;
          this.value = { ...enabled };
        }
      });

      // update our CVA `value` with changes from our `sectionForm`
      this.value = Object.assign({}, this.value, value);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  onTouched() {
    this._onTouched();
    this.ngControl.control?.updateValueAndValidity();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get formDirective(): FormGroupDirective {
    return (<FormControlName>this.ngControl).formDirective;
  }

  private _setValidators() {
    if (!this.ngControl.control) return;

    // used for identifying sectionForms in libs/ui/src/lib/helpers/touch-all.ts
    (this.ngControl.control as unknown as Record<string, unknown>)['sectionForm'] =
      this.sectionForm;

    const validators = [...this.validators, sectionValidator(this.sectionForm)];
    this.ngControl.control.setValidators(validators);
    this.ngControl.control.setAsyncValidators(this.asyncValidators);

    if (this.ngControl.touched) {
      touchAll(this.sectionForm);
    }
  }
}
